import React, { useState, useCallback } from 'react';
import { DataTable, IPaginationConfig } from 'Components/DataTable';
import { Column } from 'react-table';
import { useGetRewardHistoryQuery } from 'services';
import { IHistory } from 'Types/Interface/IHistory';
// import { CustomModal } from 'Components';
import { Box, useMediaQuery, VStack, Text, theme, Flex, Tabs, Input, Tab, TabIndicator, TabList, TabPanel, TabPanels, Icon, Button, Menu, MenuButton, MenuItem, MenuList, Center } from '@chakra-ui/react';
// import { UserActivityModalContent } from './UserActivityModalContent';
import { TransactionStatus, UserActivityType } from 'Types/Enums';
import { formatDateLong, formatDateShort, getStatusDescription, TransactionStatusComponent } from 'Utils/dataTableUtils';
import { useLocale } from 'context/LocalizationContext';
import ErrorComponent from 'Components/ErrorComponent';
import { Withdraw } from 'Components/Icons/Withdraw';
import { UserScan } from 'Components/Icons/UserScan';
import { CustomerFeedbackIcon } from 'Components/Icons/CustomerFeedbackIcon';
import useNavigation from 'Utils/navigationUtils';
import { useAppSelector } from 'Hooks';
import { BsFilter } from 'react-icons/bs';


export const UserActivity = () => {
	const { locale } = useLocale();
	const [isMobile] = useMediaQuery(`(max-width: ${theme.breakpoints.lg})`);
	const [pageIndex, setPageIndex] = useState(1);
	const params = { pageIndex, pageSize: 10, locale };
	const { data, isLoading, isFetching, isError } = useGetRewardHistoryQuery(params);
	const [searchQuery, setSearchQuery] = useState('');
	const [statusFilter, setStatusFilter] = useState<string>('all');
	const { navigateToActivityDetails } = useNavigation();
	const { userInfo } = useAppSelector((state) => state.authUser);


	const adjustStatus = (status: TransactionStatus | undefined): TransactionStatus | undefined => {
		if (status === TransactionStatus.Failed && !userInfo?.emailConfirmed) {
			return TransactionStatus.Pending;
		}
		return status;
	};

	
	const applyStatusFilter = (items: IHistory[] | undefined) => {
		if (!items) return [];
		if (statusFilter === 'all') return items;
		const filterValue = Number(statusFilter);
		return items.filter(item => adjustStatus(item.status) === filterValue);
	};

	const applySearchFilter = (items: IHistory[] | undefined) => {
		if (!items) return [];
		if (!searchQuery) return items;
		return items.filter((item) =>
			item?.productName?.toLowerCase().includes(searchQuery?.toLowerCase()) ||
			item?.brandName?.toLowerCase().includes(searchQuery?.toLowerCase())
		);
	};

	const applyFilters = (items: IHistory[] | undefined) => {
		return applySearchFilter(applyStatusFilter(items));
	};

	const Withdrawal = applyFilters(data?.data?.filter((item) => [UserActivityType.Withdrawal].includes(item.type)));
	
	const feedbacks = applyFilters(data?.data?.filter((item) => [UserActivityType.SurveyCompleted].includes(item.type)));
	const scan = applyFilters(data?.data?.filter((item) => [UserActivityType.CodeScan, UserActivityType.RepeatCodeScan].includes(item.type)));

	const newData = applyFilters(data?.data)?.map((item) => ({
		...item,
		id: item.id,
	}));


	const activityIcons: Record<UserActivityType, React.ElementType> = {
		[UserActivityType.SurveyCompleted]: CustomerFeedbackIcon,
		[UserActivityType.Withdrawal]: Withdraw,
		[UserActivityType.CodeScan]: UserScan,
		[UserActivityType.RepeatCodeScan]: UserScan,
	};

	const columns: Column<IHistory>[] = React.useMemo(
		() => [
			{
				Header: 'Date',
				accessor: 'dateCreated',
				Cell: (item) => <>{formatDateLong(item.row.original.dateCreated)}</>,
			},
			{
				Header: 'Brand',
				accessor: 'brandName',
			},
			{
				Header: 'Product',
				accessor: 'productName',
			},
			{
				Header: 'Type',
				accessor: 'type',
				Cell: (item) => <>{getStatusDescription(item.row.original.type)}</>,
			},
			{
				Header: 'Amount',
				accessor: 'points',
				Cell: (item) => <Box textAlign={'left'}>{item.row.original.points} LNT</Box>,
			},
			{
				Header: 'Status',
				accessor: 'status',
				Cell: (item) => <TransactionStatusComponent status={adjustStatus(item.row.original.status)} />,
			},
		],
		[],
	);

	const mobileColumns: Column<IHistory>[] = React.useMemo(
		() => [
			{
				accessor: 'dateCreated',
				Cell: (item) => {
					const rowData = item.row.original;
					const isWithdrawal = rowData.type === UserActivityType.Withdrawal;
					const activityType: UserActivityType = rowData.type;
					const ActivityIcon = activityIcons[activityType];

					return (
						<Flex alignItems={'center'}>
							<Flex justifyContent={'center'} align={'center'} mr={4} borderRadius={'7px'} w={'40px'} h={'40px'} bg={'loopGrey.50'}><Icon as={ActivityIcon} w={'20px'} height={'20px'}  /></Flex>
							<VStack align={'flex-start'} spacing={'3px'}>
								<Text fontSize='14px' w={'180px'} isTruncated>{isWithdrawal ? 'Withdrawal' : rowData.productName}</Text>
								<Box fontSize='12px' color='loopGrey.800'>
									By {rowData.brandName}
								</Box>
								<Box fontSize='12px' color='loopGrey.800'>
									{formatDateShort(item.row.original.dateCreated)}
								</Box>
							</VStack>
						</Flex>
					);
				},
			},
			{
				accessor: 'points',
				Cell: (item) => (
					<VStack alignItems='flex-end'>
						<Box fontWeight='400' fontSize={'14px'}>
							{item.row.original.points} LNT
						</Box>
						<TransactionStatusComponent status={adjustStatus(item.row.original.status)} />
					</VStack>
				),
			},
		],
		[],
	);

	const handleRowClick = (data: IHistory) => {
		navigateToActivityDetails(data.id || '');
	};

	const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchQuery(e.target.value);
		setPageIndex(1); 
	};


	const paginate = useCallback(({ pageIndex }: IPaginationConfig) => {
		setPageIndex(pageIndex + 1);
	}, []);

	const renderDataTable = useCallback(
		(filteredData) => {
			if (filteredData && filteredData.length === 0 && !isLoading && !isFetching) {
				return (
					<Center 
						border="1px solid" 
						borderColor="#ECEFF2" 
						borderRadius="10px" 
						p={10}
						minH="200px"
						flexDirection="column"
					>
						<Text fontSize="16px" color="loopGrey.500" mb={2}>
							No activities found
						</Text>
						<Text fontSize="14px" color="loopGrey.400">
							{statusFilter !== 'all' || searchQuery ? 'Try changing the filters or clearing the search' : 'No activities yet'}
						</Text>
					</Center>
				);
			}
			return (
				<DataTable
					data={filteredData || []}
					columns={isMobile ? mobileColumns : columns}
					onclickRow={handleRowClick}
					pageCount={data?.totalNoOfPages}
					paginate={paginate}
					initialPageIndex={0}
					isLoading={isLoading || isFetching}
					isError={isError}
					pageSize={params.pageSize}
					isMobile={isMobile}
					showCheckboxes={false}
					showMenu={false}
				/>
			);
		},
		[columns, mobileColumns, isLoading, isFetching, params.pageSize, handleRowClick, statusFilter, searchQuery, data?.totalNoOfPages, paginate]
	);

	return (
		<>
			<Tabs variant='unstyled'>
				<Flex direction={['column', 'row']} w={'100'} justifyContent={'space-between'} alignItems={['flex-start','center']}>	
					<TabList >
						<Tab fontSize={['12px', '18px']}>All</Tab>
						<Tab fontSize={['12px', '18px']}>Feedbacks</Tab>
						<Tab fontSize={['12px', '18px']}>Scan</Tab>
						<Tab fontSize={['12px', '18px']}>Withdrawal</Tab>
					</TabList>
					{!isMobile && (
						<Flex gap={2} alignItems={'center'}>
							<Menu>
								<MenuButton
									as={Button}
									rightIcon={<BsFilter />}
									_focus={{ outline: 'none' }}
									border={'1px solid #C1C8CF'}
									borderRadius={'10px'}
									px={2}
									py={3}
									fontSize="12px"
									color={'#121B26'}
									bgColor="white"
									mb={'20px'}
								>
									{statusFilter === 'all' ? 'Filter' : 
										statusFilter === String(TransactionStatus.Pending) ? 'Pending' :
										statusFilter === String(TransactionStatus.Completed) ? 'Completed' :
										statusFilter === String(TransactionStatus.Failed) ? 'Failed' :
										statusFilter === String(TransactionStatus.Canceled) ? 'Canceled' : 'Status'
									}
								</MenuButton>
								<MenuList>
									<MenuItem onClick={() => setStatusFilter('all')}>All Status</MenuItem>
									<MenuItem onClick={() => setStatusFilter(String(TransactionStatus.Pending))}>Pending</MenuItem>
									<MenuItem onClick={() => setStatusFilter(String(TransactionStatus.Completed))}>Completed</MenuItem>
									<MenuItem onClick={() => setStatusFilter(String(TransactionStatus.Failed))}>Failed</MenuItem>
									<MenuItem onClick={() => setStatusFilter(String(TransactionStatus.Canceled))}>Canceled</MenuItem>
								</MenuList>
							</Menu>
							<Input
								placeholder="Search activities..."
								value={searchQuery}
								onChange={handleSearch}
								w={'250px'}
								size="md"
								mb={'20px'}
								border={'1px solid #C1C8CF'}
								borderRadius={'10px'}
							/>
						</Flex>
					)}
				</Flex>
				<TabIndicator mt={['-2.5px','-13px']}  height='2px'  bg={'darkCoral'} borderRadius='1px' />
				{isMobile && (
					<Flex gap={2} mt={'15px'} w="100%">
						<Menu>
							<MenuButton
								as={Button}
								_focus={{ outline: 'none' }}
								border={'1px solid #C1C8CF'}
								borderRadius={'5px'}
								px={3}
								py={2}
								bgColor={statusFilter !== 'all' ? '#FF6F61' : 'white'}
								color={statusFilter !== 'all' ? 'white' : '#121B26'}
								size="sm"
								minW="50px"
							>
								<BsFilter fontSize='1.4em' />
							</MenuButton>
							<MenuList>
								<MenuItem onClick={() => setStatusFilter('all')}>All Status</MenuItem>
								<MenuItem onClick={() => setStatusFilter(String(TransactionStatus.Pending))}>Pending</MenuItem>
								<MenuItem onClick={() => setStatusFilter(String(TransactionStatus.Completed))}>Completed</MenuItem>
								<MenuItem onClick={() => setStatusFilter(String(TransactionStatus.Failed))}>Failed</MenuItem>
								<MenuItem onClick={() => setStatusFilter(String(TransactionStatus.Canceled))}>Canceled</MenuItem>
							</MenuList>
						</Menu>
						<Input
							placeholder="Search activities..."
							value={searchQuery}
							onChange={handleSearch}
							flex={1}
							borderRadius={'5px'}
							size="sm"
							border={'1px solid #C1C8CF'}
						/>
					</Flex>
				)}
				<TabPanels >
					<TabPanel p={0}>{isError ? <ErrorComponent height={['250px', null, '400px']} /> : renderDataTable(newData)}</TabPanel>
					<TabPanel p={0}>{isError ? <ErrorComponent height={['250px', null, '400px']} /> : renderDataTable(feedbacks)}</TabPanel>
					<TabPanel p={0}>{isError ? <ErrorComponent height={['250px', null, '400px']} /> : renderDataTable(scan)}</TabPanel>
					<TabPanel p={0}>{isError ? <ErrorComponent height={['250px', null, '400px']} /> : renderDataTable(Withdrawal)}</TabPanel>
				</TabPanels>
			</Tabs>
		</>
	);
};
